/**
 * @module @fanini/shared-api/client
 * Typisierter API-Client mit automatischer Clerk-Authentifizierung.
 */

/** API-Fehler mit HTTP-Statuscode */
export class ApiRequestError extends Error {
  constructor(
    public readonly statusCode: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

/** Typisierter API-Client für alle HTTP-Methoden */
export interface ApiClient {
  /** HTTP GET — Ressource abrufen */
  get<T>(path: string): Promise<T>;
  /** HTTP POST — Ressource erstellen */
  post<T>(path: string, body?: unknown): Promise<T>;
  /** HTTP PUT — Ressource vollständig ersetzen */
  put<T>(path: string, body?: unknown): Promise<T>;
  /** HTTP PATCH — Ressource teilweise aktualisieren */
  patch<T>(path: string, body?: unknown): Promise<T>;
  /** HTTP DELETE — Ressource löschen */
  delete<T>(path: string): Promise<T>;
  /** HTTP POST mit FormData (multipart/form-data) — Datei-Upload */
  upload<T>(path: string, formData: FormData): Promise<T>;
}

/**
 * Erstellt einen typisierten API-Client mit automatischer Clerk-Authentifizierung.
 *
 * @param baseUrl - Basis-URL der API (z.B. "https://api.fanini.live" oder "http://localhost:8787")
 * @param getToken - Funktion die den aktuellen Clerk JWT-Token zurückgibt
 * @returns Typisierter API-Client
 *
 * @example
 * ```tsx
 * const client = createApiClient('https://api.fanini.live', clerk.session?.getToken ?? (() => Promise.resolve(null)));
 * const events = await client.get<Event[]>('/api/events');
 * ```
 */
export function createApiClient(
  baseUrl: string,
  getToken: () => Promise<string | null>,
): ApiClient {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await getToken();

    const headers: Record<string, string> = {};

    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${baseUrl.replace(/\/$/, '')}${path}`;

    const res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
      let message = `HTTP ${res.status}: ${res.statusText}`;
      try {
        const errBody = (await res.json()) as { message?: string; error?: string };
        message = errBody.message ?? errBody.error ?? message;
      } catch {
        // Fehler-Body konnte nicht geparst werden — Fallback auf HTTP-Status
      }
      throw new ApiRequestError(res.status, message);
    }

    // 204 No Content hat keinen Body
    if (res.status === 204) {
      return undefined as T;
    }

    return res.json() as Promise<T>;
  }

  async function upload<T>(path: string, formData: FormData): Promise<T> {
    const token = await getToken();

    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const url = `${baseUrl.replace(/\/$/, '')}${path}`;

    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!res.ok) {
      let message = `HTTP ${res.status}: ${res.statusText}`;
      try {
        const errBody = (await res.json()) as { message?: string; error?: string };
        message = errBody.message ?? errBody.error ?? message;
      } catch {
        // Fehler-Body konnte nicht geparst werden — Fallback auf HTTP-Status
      }
      throw new ApiRequestError(res.status, message);
    }

    if (res.status === 204) {
      return undefined as T;
    }

    return res.json() as Promise<T>;
  }

  return {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
    delete: <T>(path: string) => request<T>('DELETE', path),
    upload,
  };
}
